import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import {
  BookOpen,
  Search,
  Plus,
  Pencil,
  Trash2,
  ArrowLeft,
  X,
  AlertTriangle,
  CheckCircle2,
  Image,
  Video,
} from "lucide-react";
import ExerciseFormDialog from "@/components/exercises/ExerciseFormDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CATEGORY_PILLS = [
  { id: "all", label: "All" },
  { id: "missing_media", label: "Missing Media / Instructions" },
  { id: "chest", label: "Chest" },
  { id: "back", label: "Back" },
  { id: "shoulders", label: "Shoulders" },
  { id: "arms", label: "Arms" },
  { id: "legs", label: "Legs" },
  { id: "core", label: "Core" },
  { id: "cardio", label: "Cardio" },
];

export default function AdminExercisesSection({
  onBack,
  initialFilter = "all",
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(initialFilter);
  const [showForm, setShowForm] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [deletingExercise, setDeletingExercise] = useState(null);

  const { data: exercises = [], isLoading } = useQuery({
    queryKey: ["admin_exercises"],
    queryFn: () => base44.entities.Exercises.list("name", 1500),
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Exercises.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_exercises"] });
      setShowForm(false);
      setEditingExercise(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Exercises.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_exercises"] });
      setShowForm(false);
      setEditingExercise(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Exercises.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_exercises"] });
      setDeletingExercise(null);
    },
  });

  const handleFormSubmit = (data) => {
    if (editingExercise) {
      updateMutation.mutate({ id: editingExercise.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const filteredExercises = useMemo(() => {
    return exercises.filter((ex) => {
      const q = search.toLowerCase().trim();
      const matchSearch =
        !q ||
        (ex.name && ex.name.toLowerCase().includes(q)) ||
        (ex.muscles_primary && ex.muscles_primary.toLowerCase().includes(q)) ||
        (Array.isArray(ex.targetMuscles) &&
          ex.targetMuscles.some((m) => m.toLowerCase().includes(q)));

      if (!matchSearch) return false;

      const hasMedia = Boolean(ex.gifUrl || ex.gif_url || ex.video_url);
      const hasInstructions = Array.isArray(ex.instructions) && ex.instructions.length > 0;
      const isMissingMediaOrInstructions = !hasMedia || !hasInstructions;

      if (filter === "missing_media") return isMissingMediaOrInstructions;
      if (filter === "all") return true;

      const bodyPart = (ex.body_part || ex.category || (Array.isArray(ex.bodyParts) ? ex.bodyParts[0] : "") || "").toLowerCase();
      if (filter === "chest") return bodyPart.includes("chest");
      if (filter === "back") return bodyPart.includes("back");
      if (filter === "shoulders") return bodyPart.includes("shoulder") || bodyPart.includes("neck");
      if (filter === "arms") return bodyPart.includes("arm");
      if (filter === "legs") return bodyPart.includes("leg");
      if (filter === "core") return bodyPart.includes("waist") || bodyPart.includes("core") || bodyPart.includes("abs");
      if (filter === "cardio") return bodyPart.includes("cardio");

      return true;
    });
  }, [exercises, search, filter]);

  return (
    <div className="space-y-4 pb-20">
      {/* Top Bar with Back navigation & Title */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="p-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-all cursor-pointer"
            aria-label="Back to Command Center"
          >
            <ArrowLeft className="w-4 h-4 text-sky-400" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white leading-tight">Exercise Library</h1>
            <p className="text-[11px] text-slate-400">
              {filteredExercises.length} of {exercises.length} exercises
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingExercise(null);
            setShowForm(true);
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs shadow-md active:scale-95 transition-all cursor-pointer"
          data-testid="add-exercise-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Add Exercise</span>
        </button>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by exercise name or muscle…"
          className="w-full pl-9 pr-8 py-2 rounded-xl bg-[#071326] border border-white/10 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-sky-400"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Filter Horizontal Pills */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
        {CATEGORY_PILLS.map((pill) => {
          const isActive = filter === pill.id;
          return (
            <button
              key={pill.id}
              type="button"
              onClick={() => setFilter(pill.id)}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                isActive
                  ? "bg-sky-500 text-slate-950 shadow-sm"
                  : "bg-white/5 text-slate-400 hover:text-white border border-white/5"
              }`}
            >
              {pill.label}
            </button>
          );
        })}
      </div>

      {/* Exercise Cards List */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 rounded-2xl bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : filteredExercises.length === 0 ? (
        <div className="rounded-2xl p-8 bg-[#071326] border border-white/10 text-center space-y-2">
          <BookOpen className="w-8 h-8 text-slate-500 mx-auto" />
          <p className="text-xs font-bold text-white">No exercises found</p>
          <p className="text-[11px] text-slate-400">
            {search ? "No exercises match your search query." : "No records match the active filter."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredExercises.slice(0, 100).map((exercise) => {
            const hasMedia = Boolean(exercise.gifUrl || exercise.gif_url || exercise.video_url);
            const hasInstructions =
              Array.isArray(exercise.instructions) && exercise.instructions.length > 0;
            const primaryMuscle =
              exercise.muscles_primary ||
              (Array.isArray(exercise.targetMuscles) ? exercise.targetMuscles[0] : "") ||
              exercise.body_part ||
              "";

            return (
              <div
                key={exercise.id}
                className="p-3 rounded-2xl bg-gradient-to-r from-[#08152B] to-[#040C1A] border border-white/10 hover:border-sky-400/40 transition-all flex items-center justify-between gap-3 shadow-sm"
                data-testid={`exercise-card-${exercise.id}`}
              >
                {/* Left: Thumbnail / Media icon + Details */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
                    {exercise.gifUrl || exercise.gif_url ? (
                      <img
                        src={exercise.gifUrl || exercise.gif_url}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <BookOpen className="w-5 h-5 text-slate-500" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-white truncate capitalize">
                      {exercise.name}
                    </p>
                    {primaryMuscle && (
                      <p className="text-[10px] text-sky-400 font-semibold capitalize truncate">
                        {primaryMuscle}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span
                        className={`text-[9px] font-bold px-1.5 py-0.2 rounded-full flex items-center gap-1 ${
                          hasMedia
                            ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20"
                            : "bg-amber-500/15 text-amber-300 border border-amber-500/20"
                        }`}
                      >
                        {hasMedia ? (
                          <>
                            <CheckCircle2 className="w-2.5 h-2.5" /> GIF
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="w-2.5 h-2.5" /> No GIF
                          </>
                        )}
                      </span>

                      <span
                        className={`text-[9px] font-bold px-1.5 py-0.2 rounded-full flex items-center gap-1 ${
                          hasInstructions
                            ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20"
                            : "bg-amber-500/15 text-amber-300 border border-amber-500/20"
                        }`}
                      >
                        {hasInstructions ? (
                          <>
                            <CheckCircle2 className="w-2.5 h-2.5" /> Guide
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="w-2.5 h-2.5" /> No Guide
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right: Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingExercise(exercise);
                      setShowForm(true);
                    }}
                    className="p-2 rounded-xl bg-white/5 hover:bg-sky-500/20 text-slate-300 hover:text-sky-300 border border-white/10 active:scale-95 transition-all cursor-pointer"
                    aria-label="Edit Exercise"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeletingExercise(exercise)}
                    className="p-2 rounded-xl bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-400 border border-white/10 active:scale-95 transition-all cursor-pointer"
                    aria-label="Delete Exercise"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form Dialog */}
      {showForm && (
        <ExerciseFormDialog
          open={showForm}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setShowForm(false);
              setEditingExercise(null);
            }
          }}
          initialData={editingExercise}
          onSubmit={handleFormSubmit}
        />
      )}

      {/* Delete Confirmation */}
      {deletingExercise && (
        <AlertDialog open onOpenChange={() => setDeletingExercise(null)}>
          <AlertDialogContent className="bg-[#030A17] border border-white/10 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Exercise</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-300 text-xs">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-white capitalize">
                  {deletingExercise.name}
                </span>
                ? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteMutation.mutate(deletingExercise.id)}
                className="bg-red-500 text-white hover:bg-red-600"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
